import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import type { SearchHit, SearchType } from '@brandschutz/shared';
import { Icon, type IconName } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { useDebounce } from '@/lib/use-debounce';
import { modalBackdrop, modalContent, modalTransition } from '@/lib/motion';
import { useCommandPalette } from './context';
import { search } from './api';

const TYPE_ORDER: SearchType[] = ['customer', 'project', 'contact', 'document'];

const TYPE_META: Record<SearchType, { label: string; icon: IconName }> = {
  customer: { label: 'Kunden', icon: 'Building2' },
  project: { label: 'Projekte', icon: 'FolderClosed' },
  contact: { label: 'Ansprechpartner', icon: 'Contact2' },
  document: { label: 'Dokumente', icon: 'FileText' },
};

function buildNavigationTarget(hit: SearchHit): string {
  switch (hit.type) {
    case 'customer':
      return `/customers/${hit.id}`;
    case 'project':
      return `/projects/${hit.id}`;
    case 'contact': {
      // Es gibt keine dedizierte Kontakt-Detailseite. Das Backend liefert
      // meta.customerId mit — wir springen direkt zur Kunden-Detailseite;
      // ohne zugeordneten Kunden fällt die Navigation auf die Kontaktliste
      // zurück.
      const customerId = hit.meta?.customerId;
      if (typeof customerId === 'number') return `/customers/${customerId}`;
      return '/contacts';
    }
    case 'document': {
      const projectId = hit.meta?.projectId;
      if (typeof projectId === 'number') return `/projects/${projectId}`;
      return '/documents';
    }
  }
}

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebounce(query.trim(), 200);

  // Global Hotkey: Strg/Cmd+K toggelt die Palette; Esc schließt.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Body-Scroll-Lock + Auto-Focus auf das Input
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open]);

  // Beim Schließen: Query + Selection zurücksetzen, damit der nächste Aufruf
  // sauber startet.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['command-search', debounced],
    queryFn: () => search(debounced),
    enabled: open && debounced.length >= 1,
    staleTime: 1000 * 30,
  });

  // Flache Liste in Render-Reihenfolge für Pfeiltasten-Navigation.
  const flatHits = useMemo<SearchHit[]>(() => {
    if (!data) return [];
    const out: SearchHit[] = [];
    for (const t of TYPE_ORDER) {
      const group = data.groups[t] ?? [];
      for (const h of group) out.push(h);
    }
    return out;
  }, [data]);

  // Active-Index in gültigem Bereich halten, sobald sich die Ergebnisse ändern.
  useEffect(() => {
    setActiveIndex((i) => {
      if (flatHits.length === 0) return 0;
      if (i >= flatHits.length) return flatHits.length - 1;
      return i;
    });
  }, [flatHits]);

  const handleSelect = (hit: SearchHit) => {
    const target = buildNavigationTarget(hit);
    setOpen(false);
    navigate(target);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (flatHits.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatHits.length);
    } else if (e.key === 'ArrowUp') {
      if (flatHits.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatHits.length) % flatHits.length);
    } else if (e.key === 'Enter') {
      if (flatHits.length === 0) return;
      e.preventDefault();
      const hit = flatHits[activeIndex];
      if (hit) handleSelect(hit);
    }
  };

  // Scroll aktive Zeile in den sichtbaren Bereich.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-hit-index="${activeIndex}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const showEmptyHint = debounced.length === 0;
  const showNoResults =
    !showEmptyHint && !isFetching && flatHits.length === 0 && data !== undefined;

  // Globaler Render-Index über alle Gruppen hinweg, damit die Pfeiltasten-
  // Navigation und das visuelle Highlight synchron bleiben.
  let renderIndex = -1;

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={modalBackdrop}
          transition={modalTransition}
        >
          <button
            type="button"
            aria-label="Schnellsuche schließen"
            onClick={() => setOpen(false)}
            className="absolute inset-0 cursor-default bg-ink-900/40 backdrop-blur-sm"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Schnellsuche"
            variants={modalContent}
            transition={modalTransition}
            className="relative flex w-full max-w-[600px] flex-col overflow-hidden rounded-lg border border-ink-200 bg-paper shadow-elev-4"
            style={{ maxHeight: '80vh' }}
          >
            {/* Search-Input */}
            <div className="flex items-center gap-2.5 border-b border-ink-200 px-4 py-3">
              <Icon name="Search" size={18} className="text-ink-500" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder="Suchen — Kunden, Projekte, Ansprechpartner, Dokumente…"
                className="flex-1 bg-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
                aria-label="Suchbegriff"
                autoComplete="off"
                spellCheck={false}
              />
              {isFetching ? (
                <Icon name="Loader2" size={16} className="animate-spin text-ink-400" />
              ) : null}
            </div>

            {/* Results / Hint */}
            <div ref={listRef} className="flex-1 overflow-y-auto py-2">
              {showEmptyHint ? <EmptyHint /> : null}

              {showNoResults ? (
                <div className="px-4 py-6 text-center text-sm text-ink-500">
                  Keine Treffer für „{debounced}".
                </div>
              ) : null}

              {!showEmptyHint && data
                ? TYPE_ORDER.map((type) => {
                    const group = data.groups[type] ?? [];
                    if (group.length === 0) return null;
                    const meta = TYPE_META[type];
                    return (
                      <div key={type} className="mb-2">
                        <div className="px-4 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-ink-500">
                          {meta.label}
                        </div>
                        <ul>
                          {group.map((hit) => {
                            renderIndex += 1;
                            const isActive = renderIndex === activeIndex;
                            const currentIndex = renderIndex;
                            return (
                              <li key={`${hit.type}-${hit.id}`}>
                                <button
                                  type="button"
                                  data-hit-index={currentIndex}
                                  onClick={() => handleSelect(hit)}
                                  onMouseEnter={() => setActiveIndex(currentIndex)}
                                  className={cn(
                                    'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors',
                                    isActive
                                      ? 'bg-ink-100 ring-1 ring-inset ring-ink-300'
                                      : 'hover:bg-ink-50',
                                  )}
                                >
                                  <Icon name={meta.icon} size={16} className="text-ink-500" />
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm text-ink-900">{hit.title}</div>
                                    {hit.subtitle ? (
                                      <div className="truncate text-xs text-ink-500">
                                        {hit.subtitle}
                                      </div>
                                    ) : null}
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })
                : null}
            </div>

            {/* Footer-Hint mit Shortcuts */}
            <div className="flex items-center justify-between gap-3 border-t border-ink-200 bg-ink-50 px-4 py-2 text-[11px] text-ink-500">
              <span className="flex items-center gap-3">
                <ShortcutHint keys={['↑', '↓']} label="navigieren" />
                <ShortcutHint keys={['Enter']} label="öffnen" />
                <ShortcutHint keys={['Esc']} label="schließen" />
              </span>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function EmptyHint() {
  return (
    <div className="px-4 py-3">
      <div className="rounded border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
        <div className="mb-2 font-medium text-ink-800">Tipps zur Schnellsuche</div>
        <ul className="space-y-1">
          <li className="flex items-center gap-2">
            <ShortcutHint keys={['↑', '↓']} label="zwischen Treffern wechseln" />
          </li>
          <li className="flex items-center gap-2">
            <ShortcutHint keys={['Enter']} label="Treffer öffnen" />
          </li>
          <li className="flex items-center gap-2">
            <ShortcutHint keys={['Esc']} label="Suche schließen" />
          </li>
          <li className="pt-1 text-ink-500">Tipp: „huber" findet Kunden + Ansprechpartner.</li>
        </ul>
      </div>
    </div>
  );
}

function ShortcutHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k) => (
        <kbd
          key={k}
          className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 font-mono text-[10px] text-ink-700"
        >
          {k}
        </kbd>
      ))}
      <span className="text-ink-500">{label}</span>
    </span>
  );
}
